/**
 * Générateur — Automatisme : donner la nature d'une expression littérale :
 * 3x + 2 est une somme, 5(x + 4) est un produit.
 * Attendu ID : 1229 | Niveau : 3ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 11, ligne 797).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * La DERNIÈRE OPÉRATION décide : somme (3x + 2, 2x² + 1, x + 5) ou produit
 * (5(x + 4), 3(2x − 1), (x + 2)(x + 3), 3x, x(x + 1)). Le mapping prévoyait
 * une EXTRACTION de gen_158 (4e) ; ses cartes écrivent en texte brut avec
 * l'indice « cherche la dernière opération » dans la consigne : ici un gen
 * neuf compact aux mêmes règles, tout en KaTeX, l'indice réservé au coup de
 * pouce (écart au mapping, À ARBITRER). Deux natures seulement (les mots du
 * programme) ; le quotient est un troisième choix jamais juste (À ARBITRER),
 * la différence est une somme (3x − 2 = 3x + (−2), pas de carte à part) ;
 * les carrés (x + 1)² exclus. Aucune activité MathsMentales : variables du
 * programme et des attendus de fin de 4e (« 3x + 12 somme, 3(x + 4)
 * produit »).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Somme ou produit ? » (nature_qcm_unique) : « 5(x + 4) est… »
 *     [un produit ; une somme ; un quotient].
 *   Carte 2 « Laquelle est un produit ? » (laquelle_qcm_unique) : quatre
 *     expressions, une seule de la nature demandée (somme ou produit,
 *     contenu.sousType), les trois autres de l'autre nature.
 *   Carte 3 « Vrai ou faux » (nature_tableau_vf) : quatre lignes « 3x + 2
 *     est une somme. » / « 3(x + 4) est une somme. » (F).
 *
 * INVARIANTS : la nature est celle de la FORME construite (assert : une
 * forme somme a un + ou un − hors parenthèses, une forme produit n'en a pas)
 * ; expressions distinctes dans un QCM.
 * SACRIFIÉS (fiche « la dernière opération ») : 3x + 2 et 5(x + 4), les
 * exemples du programme, toute lettre ; auto-testés par gen1229.self.test.js.
 *
 * ── Erreurs (familles nommées, inférences) ──────────────────────────────
 *   plus_dedans          : 5(x + 4) → somme (le + dans la parenthèse), PRINCIPALE ;
 *   fois_dedans          : 3x + 2 → produit (le 3x) ;
 *   parentheses_donc_produit : (x + 2) + 3 → produit.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, tex,
} from '../../../utils';

const ATTENDU_ID = 1229;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_QCM = 'Somme ou produit ?';
const L_LAQUELLE = 'Laquelle est un produit ?';
const L_VF = 'Vrai ou faux';

const LETTRES = ['x', 'x', 'x', 'n', 't', 'y'];

// ─── Le domaine : des formes, chacune avec sa nature ──────────────────────

/** Les exemples du programme et de la fiche, toute lettre. */
export const SACRIFIES = [{ forme: 'ax_b', a: 3, b: 2 }, { forme: 'a_paren_x_b', a: 5, b: 4 }];

const FORMES = {
    ax_b: { nature: 'somme', tex: (a, b, L) => `${a}${L} + ${b}` },
    ax_moins_b: { nature: 'somme', tex: (a, b, L) => `${a}${L} - ${b}` },
    b_ax: { nature: 'somme', tex: (a, b, L) => `${b} + ${a}${L}` },
    x_b: { nature: 'somme', tex: (a, b, L) => `${L} + ${b}` },
    ax2_b: { nature: 'somme', tex: (a, b, L) => `${a}${L}^{2} + ${b}` },
    paren_plus_b: { nature: 'somme', tex: (a, b, L) => `${a}(${L} + 1) + ${b}` },
    a_paren_x_b: { nature: 'produit', tex: (a, b, L) => `${a}(${L} + ${b})` },
    a_paren_x_moins_b: { nature: 'produit', tex: (a, b, L) => `${a}(${L} - ${b})` },
    a_paren_ax_b: { nature: 'produit', tex: (a, b, L) => `${a}(${a + 1}${L} + ${b})` },
    paren_paren: { nature: 'produit', tex: (a, b, L) => `(${L} + ${a})(${L} + ${b})` },
    ax: { nature: 'produit', tex: (a, b, L) => `${a}${L}` },
    x_paren: { nature: 'produit', tex: (a, b, L) => `${L}(${L} + ${b})` },
};
const SOMMES = Object.keys(FORMES).filter((f) => FORMES[f].nature === 'somme');
const PRODUITS = Object.keys(FORMES).filter((f) => FORMES[f].nature === 'produit');

const estSacrifie = (forme, a, b) => SACRIFIES.some((s) => s.forme === forme && s.a === a && s.b === b);

/** Assert sur l'écriture : une somme a un + ou un − hors parenthèses, un produit n'en a pas. */
const verifier = (forme, ecriture) => {
    const horsParentheses = ecriture.replace(/\([^)]*\)/g, '');
    const aSigne = /[+-]/.test(horsParentheses);
    if (aSigne !== (FORMES[forme].nature === 'somme')) throw new Error(`gen_1229 : nature de ${ecriture}`);
};

/** Une expression { forme, a, b, nature, ecriture } ; `nature` impose somme ou produit. */
const tirer = (L, { nature = null, exclure = [] } = {}) => {
    for (let essai = 0; essai < 200; essai++) {
        const forme = randomChoice(nature === 'somme' ? SOMMES : nature === 'produit' ? PRODUITS : [...SOMMES, ...PRODUITS]);
        const a = randInt(2, 9);
        const b = randInt(1, 9);
        if (estSacrifie(forme, a, b)) continue;
        const ecriture = FORMES[forme].tex(a, b, L);
        if (exclure.includes(ecriture)) continue;
        verifier(forme, ecriture);
        return { forme, a, b, nature: FORMES[forme].nature, ecriture };
    }
    return nature === 'produit' ? { forme: 'a_paren_x_b', a: 3, b: 7, nature: 'produit', ecriture: `3(${L} + 7)` } : { forme: 'ax_b', a: 4, b: 7, nature: 'somme', ecriture: `4${L} + 7` };
};

const MOT = { somme: 'une somme', produit: 'un produit', quotient: 'un quotient' };
const EXPLICATIONS = {
    plus_dedans: 'Le + est dans la parenthèse : la DERNIÈRE opération, c\'est la multiplication par 5. 5(x + 4) est un produit.',
    fois_dedans: '3x se calcule d\'abord, puis on ajoute 2 : la dernière opération est l\'addition, 3x + 2 est une somme.',
    parentheses_donc_produit: 'Des parenthèses ne font pas un produit : (x + 2) + 3 ajoute encore 3, c\'est une somme.',
    quotient: 'Il n\'y a pas de division : ce n\'est pas un quotient.',
    regle: 'Je cherche la dernière opération que je ferais en calculant : un + ou un − à la fin, c\'est une somme ; un × (souvent caché devant une parenthèse), c\'est un produit.',
};

// ─── Carte 1 : Somme ou produit ? (QCM) ───────────────────────────────────

const genQcm = () => {
    const L = randomChoice(LETTRES);
    const it = tirer(L);
    const bon = MOT[it.nature];
    const payload = makeQcm([bon, MOT[it.nature === 'somme' ? 'produit' : 'somme'], MOT.quotient], bon, `<p><b>${tex(it.ecriture)} est…</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, sousType: it.nature, explications: EXPLICATIONS };
    payload._meta.params = { ...it, lettre: L, distracteurs: [{ code: it.nature === 'produit' ? 'plus_dedans' : 'fois_dedans' }, { code: 'quotient' }] };
    return payload;
};

// ─── Carte 2 : Laquelle est un produit ? (QCM) ────────────────────────────

const genLaquelle = () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        const nature = randomChoice(['produit', 'produit', 'somme']);
        const autre = nature === 'produit' ? 'somme' : 'produit';
        const bon = tirer(L, { nature });
        const faux = [];
        for (let i = 0; i < 3; i++) faux.push(tirer(L, { nature: autre, exclure: [bon.ecriture, ...faux.map((f) => f.ecriture)] }));
        const choix = [bon, ...faux].map((it) => tex(it.ecriture));
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, choix[0], `<p><b>Laquelle de ces expressions est ${MOT[nature]} ?</b></p>`, opts(L_LAQUELLE));
        payload.contenu = { ...payload.contenu, sousType: nature, explications: EXPLICATIONS };
        payload._meta.params = { ...bon, lettre: L, distracteurs: faux.map((f) => ({ code: nature === 'produit' ? (f.forme === 'paren_plus_b' ? 'parentheses_donc_produit' : 'fois_dedans') : 'plus_dedans', forme: f.forme })) };
        return payload;
    }
    const choix = [tex('3(x + 7)'), tex('4x + 7'), tex('x + 5'), tex('2x^{2} + 1')];
    const payload = makeQcm(choix, choix[0], '<p><b>Laquelle de ces expressions est un produit ?</b></p>', opts(L_LAQUELLE));
    payload.contenu = { ...payload.contenu, sousType: 'produit', explications: EXPLICATIONS };
    payload._meta.params = { forme: 'a_paren_x_b', a: 3, b: 7, nature: 'produit', lettre: 'x', distracteurs: [{ code: 'fois_dedans' }, { code: 'fois_dedans' }, { code: 'fois_dedans' }] };
    return payload;
};

// ─── Carte 3 : Vrai ou faux ───────────────────────────────────────────────

const genVf = () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        const items = [];
        for (let i = 0; i < 4; i++) items.push(tirer(L, { nature: i < 2 ? 'somme' : 'produit', exclure: items.map((it) => it.ecriture) }));
        if (new Set(items.map((it) => it.ecriture)).size < 4) continue;
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = shuffle(items.map((it, i) => {
            const dite = verites[i] ? it.nature : it.nature === 'somme' ? 'produit' : 'somme';
            return { ligne: `${tex(it.ecriture)} est ${MOT[dite]}.`, vraie: verites[i], code: verites[i] ? null : it.nature === 'produit' ? 'plus_dedans' : 'fois_dedans' };
        }));
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), lignes.map((l) => (l.vraie ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, lettre: L, verites: lignes.map((l) => l.vraie), distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    const lignes = [`${tex('4x + 7')} est une somme.`, `${tex('3(x + 7)')} est une somme.`, `${tex('x(x + 2)')} est un produit.`, `${tex('2x^{2} + 1')} est un produit.`];
    const payload = makeVraiFaux(lignes, [0, 1, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: [], lettre: 'x', verites: [true, false, true, false], distracteurs: [{ code: 'plus_dedans' }, { code: 'fois_dedans' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'deux natures, somme et produit (les mots du programme) ; le quotient est un troisième choix jamais juste (nature_qcm) ; la différence est une somme',
        'formes sommes : ax + b, ax − b, b + ax, x + b, ax² + b, a(x + 1) + b ; produits : a(x + b), a(x − b), a(ax + b), (x + a)(x + b), ax, x(x + b) ; assert sur l\'écriture (signe hors parenthèses)',
        'sacrifiés : 3x + 2 et 5(x + 4), toute lettre',
    ],
    laquelle_qcm_unique: 'quatre expressions distinctes, une seule de la nature demandée (produit deux fois sur trois), contenu.sousType ; familles plus_dedans, fois_dedans, parentheses_donc_produit',
    vf_tableau_vf: 'quatre lignes « … est une somme / un produit », deux sommes et deux produits, une à trois vraies',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    nature_qcm_unique: genQcm,
    laquelle_qcm_unique: genLaquelle,
    nature_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'nature', label: L_QCM, formatsList: [
        { key: 'nature_qcm_unique', label: 'QCM' },
        { key: 'nature_tableau_vf', label: 'Vrai/Faux' },
    ] },
    { id: 'laquelle', label: L_LAQUELLE, formatsList: [{ key: 'laquelle_qcm_unique', label: 'QCM' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
